import { useEffect, useMemo, useState } from 'react';
import {
  canPublish, fetchNotices, fetchNextEvents, fetchBoard, fetchWeather,
  publishNotice, addEvent, postToBoard, acknowledgeNotice,
  fetchDiary, completeDiaryEntry,
  retractNotice, deleteNotice,
  type Notice, type CompanyEvent, type BoardPost, type Weather, type DiaryEntry,
} from '../lib/today';
import { fetchInbox, type InboxItem } from '../lib/inbox';
import { useDialog } from '../lib/ui';
import { useNavigate } from 'react-router-dom';
import { Loading } from '../components/Loading';
import './today.css';
import { onFail } from '../lib/errors';

/* =====================================================================================
   Today — the single diary.

   Every deadline in the system surfaces here: patent annuities, reimbursement submission
   windows, contract notice periods, certificate expiries, tender deadlines, promises to
   pay. Deliberately one unavoidable surface rather than per-module reminders, because the
   failures that actually hurt (a missed annuity, a missed NUB window) are silent.
   ===================================================================================== */





const sevLabel: Record<string, string> = {
  critical: 'Critical', high: 'High', normal: 'Normal', low: 'Low',
};

/* daysAway in the table above was written against a fixed date, so it would drift by one
   more day every day. Recomputed from the due date instead, which cannot go stale. */
function daysAway(due: string) {
  return Math.ceil((new Date(due + 'T00:00:00').getTime() - Date.now()) / 86_400_000);
}

function bucket(d: number) {
  if (d <= 30) return 'Next 30 days';
  if (d <= 90) return 'Next quarter';
  if (d <= 365) return 'This year';
  return 'Later';
}

/* -------------------------------------------------------------------------------------
   The live strip: greeting, date, clock, weather.
   ------------------------------------------------------------------------------------- */
function greeting(h: number) {
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

const INBOX_LABEL: Record<InboxItem['kind'], string> = {
  sign: 'Signature', review: 'Review', conflict: 'Disputed figure', overdue: 'Overdue', expense: 'Expenses' };

function WaitingOnYou({ items, loading }: { items: InboxItem[]; loading: boolean }) {
  const navigate = useNavigate();
  if (loading) {
    return <section className="woy card"><Loading label="Checking what needs you" /></section>;
  }
  if (!items.length) {
    return (
      <section className="woy card woy-clear">
        <strong>Nothing is waiting on you.</strong>
        <span>Signatures, reviews, disputed figures and overdue actions all appear here.</span>
      </section>
    );
  }
  return (
    <section className="woy card">
      <header className="woy-head">
        <h2>Waiting on you</h2>
        <span className="pill warn">{items.length}</span>
      </header>
      <ul className="woy-list">
        {items.slice(0, 8).map((i) => (
          <li key={i.id}>
            <button className="woy-item" onClick={() => navigate(i.href)}>
              <span className={`pill ${i.overdue ? 'risk' : i.kind === 'conflict' ? 'warn' : 'mute'}`}>
                {INBOX_LABEL[i.kind]}
              </span>
              <span className="woy-title">{i.title}</span>
              <span className="woy-detail">{i.detail}</span>
              {i.due && (
                <span className={i.overdue ? 'woy-due overdue' : 'woy-due'}>
                  {new Date(i.due).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
      {items.length > 8 && <p className="woy-more">and {items.length - 8} more</p>}
    </section>
  );
}

function Clock({ weather }: { weather: Weather | null }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    // Ticks on the minute boundary rather than every second: this is a clock to glance at,
    // and a seconds display re-rendering 60 times a minute is wasted work on a laptop battery.
    const id = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="today-strip card">
      <div className="strip-main">
        <h1 className="strip-greeting">{greeting(now.getHours())}</h1>
        <p className="strip-date">
          {now.toLocaleDateString('en-GB', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
          })}
        </p>
      </div>
      <div className="strip-time">{now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
      {!weather && <div className="strip-weather wx-loading" aria-hidden="true">…</div>}
      {weather && (
        <div className="strip-weather" title={`Feels like ${weather.feelsC}°C`}>
          <div className="wx-temp">{weather.tempC}°</div>
          <div className="wx-meta">
            <span className="wx-desc">{weather.description}</span>
            <span className="wx-place">{weather.place}</span>
          </div>
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------------------
   Notices. Composing is shown only to SLT, but see lib/today.ts: that is a courtesy so
   nobody is offered a box that would only produce an error. The database is the control.
   ------------------------------------------------------------------------------------- */
function NoticeComposer({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<Notice['kind']>('announcement');
  const [requiresAck, setRequiresAck] = useState(false);
  const [ackDue, setAckDue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!open) {
    return (
      <button className="btn-compose" onClick={() => setOpen(true)}>
        Post a notice
      </button>
    );
  }

  async function save() {
    setErr(null); setBusy(true);
    try {
      await publishNotice({ title, body, kind, requiresAck, ackDue: ackDue || null });
      setTitle(''); setBody(''); setRequiresAck(false); setAckDue('');
      setOpen(false); onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="composer card">
      <input className="composer-title" placeholder="Notice title" value={title}
             onChange={(e) => setTitle(e.target.value)} disabled={busy} />
      <textarea className="composer-body" rows={4} placeholder="What does everyone need to know?"
                value={body} onChange={(e) => setBody(e.target.value)} disabled={busy} />
      <div className="composer-row">
        <select value={kind} onChange={(e) => setKind(e.target.value as Notice['kind'])} disabled={busy}>
          <option value="announcement">Announcement</option>
          <option value="policy">Policy</option>
          <option value="urgent">Urgent</option>
          <option value="celebration">Celebration</option>
        </select>
        <label className="composer-ack">
          <input type="checkbox" checked={requiresAck} disabled={busy}
                 onChange={(e) => setRequiresAck(e.target.checked)} />
          Requires acknowledgement
        </label>
        {/* The database refuses a notice that demands acknowledgement with no deadline, so
            the field appears the moment the box is ticked rather than failing on save. */}
        {requiresAck && (
          <input type="date" value={ackDue} disabled={busy}
                 onChange={(e) => setAckDue(e.target.value)} />
        )}
      </div>
      {err && <p className="composer-err" role="alert">{err}</p>}
      <div className="composer-actions">
        <button className="btn-cancel" onClick={() => { setOpen(false); setErr(null); }} disabled={busy}>
          Cancel
        </button>
        <button className="btn-save" onClick={save}
                disabled={busy || !title.trim() || !body.trim() || (requiresAck && !ackDue)}>
          {busy ? 'Posting' : 'Post notice'}
        </button>
      </div>
    </div>
  );
}

function Notices({ notices, slt, reload }: { notices: Notice[]; slt: boolean; reload: () => void }) {
  // Taking a notice down is deliberately a two-step action with a required reason: it was
  // visible to everyone, so its removal is accountable too.
  const [removing, setRemoving] = useState<{ n: Notice; mode: 'retract' | 'delete' } | null>(null);
  const [signing, setSigning] = useState<string | null>(null);
  const [name, setName] = useState('');

  async function sign(id: string) {
    await acknowledgeNotice(id, name);
    setSigning(null); setName(''); reload();
  }

  return (
    <section className="today-block">
      <header className="block-head">
        <h2>From management</h2>
        {slt && <NoticeComposer onDone={reload} />}
      </header>

      {!notices.length && (
        <p className="block-empty">
          No notices. {slt ? 'Post one and it appears here for everyone.' : ''}
        </p>
      )}

      {removing && (
        <RemoveNotice item={removing} onClose={() => setRemoving(null)}
                      onDone={() => { setRemoving(null); reload(); }} />
      )}

      {notices.map((n) => (
        <article key={n.id} className={`card notice notice-${n.kind}`}>
          <header className="notice-head">
            <h3>{n.title}</h3>
            <div className="notice-tags">
              {n.pinned && <span className="pill warn">Pinned</span>}
              <span className={`pill ${n.kind === 'urgent' ? 'risk' : n.kind === 'celebration' ? 'ok' : 'mute'}`}>
                {n.kind}
              </span>
            </div>
          </header>
          <p className="notice-body">{n.body}</p>
          <footer className="notice-foot">
            <span>{n.author_name ?? 'Management'}</span>
            <span>{new Date(n.publish_at).toLocaleDateString('en-GB',
              { day: 'numeric', month: 'long' })}</span>
            {n.requires_ack && !n.acknowledged && (
              signing === n.id ? (
                <span className="ack-sign">
                  <input placeholder="Type your full name" value={name}
                         onChange={(e) => setName(e.target.value)} />
                  <button disabled={name.trim().length < 3} onClick={() => sign(n.id)}>
                    Confirm
                  </button>
                </span>
              ) : (
                <button className="ack-btn" onClick={() => setSigning(n.id)}>
                  Acknowledge{n.ack_due ? ` by ${new Date(n.ack_due).toLocaleDateString('en-GB',
                    { day: 'numeric', month: 'short' })}` : ''}
                </button>
              )
            )}
            {n.requires_ack && n.acknowledged && <span className="pill ok">Acknowledged</span>}
            {/* Anyone who can publish to the whole company can take a notice down, plus its
                author. If something offensive is on the front page, whoever is present must
                be able to remove it without first finding an owner. */}
            {slt && (
              <span className="notice-remove">
                <button className="link-btn" onClick={() => setRemoving({ n, mode: 'retract' })}>
                  Take down
                </button>
                <button className="link-btn danger" onClick={() => setRemoving({ n, mode: 'delete' })}>
                  Delete
                </button>
              </span>
            )}
          </footer>
        </article>
      ))}
    </section>
  );
}

/* -------------------------------------------------------------------------------------
   Next event and the social board
   ------------------------------------------------------------------------------------- */
function daysUntil(iso: string) {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

function NextEvents({ events, slt, reload }: { events: CompanyEvent[]; slt: boolean; reload: () => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: '', kind: 'company' as CompanyEvent['kind'], startsAt: '', location: '' });
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setErr(null);
    try {
      await addEvent({ title: f.title, kind: f.kind, startsAt: new Date(f.startsAt).toISOString(),
                       location: f.location });
      setF({ title: '', kind: 'company', startsAt: '', location: '' });
      setOpen(false); reload();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  return (
    <section className="today-block">
      <header className="block-head">
        <h2>Coming up</h2>
        {slt && <button className="btn-compose" onClick={() => setOpen(!open)}>Add event</button>}
      </header>

      {open && (
        <div className="composer card">
          <input placeholder="Event title" value={f.title}
                 onChange={(e) => setF({ ...f, title: e.target.value })} />
          <div className="composer-row">
            <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as CompanyEvent['kind'] })}>
              <option value="company">Company</option>
              <option value="congress">Congress</option>
              <option value="board">Board</option>
              <option value="deadline">Deadline</option>
              <option value="training">Training</option>
            </select>
            <input type="datetime-local" value={f.startsAt}
                   onChange={(e) => setF({ ...f, startsAt: e.target.value })} />
            <input placeholder="Location" value={f.location}
                   onChange={(e) => setF({ ...f, location: e.target.value })} />
          </div>
          {err && <p className="composer-err" role="alert">{err}</p>}
          <div className="composer-actions">
            <button className="btn-cancel" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-save" disabled={!f.title.trim() || !f.startsAt} onClick={save}>
              Add event
            </button>
          </div>
        </div>
      )}

      {!events.length && <p className="block-empty">Nothing in the diary yet.</p>}

      {events.map((e, i) => (
        <article key={e.id} className={`card event ${i === 0 ? 'event-next' : ''}`}>
          <div className="event-when">
            <span className="event-d">{new Date(e.starts_at).toLocaleDateString('en-GB', { day: 'numeric' })}</span>
            <span className="event-m">{new Date(e.starts_at).toLocaleDateString('en-GB', { month: 'short' })}</span>
          </div>
          <div className="event-main">
            <h3>{e.title}</h3>
            <p className="event-meta">
              <span className="pill mute">{e.kind}</span>
              {e.location && <span>{e.location}</span>}
              <span>{daysUntil(e.starts_at)} days</span>
            </p>
          </div>
        </article>
      ))}
    </section>
  );
}

function Noticeboard({ posts, reload }: { posts: BoardPost[]; reload: () => void }) {
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<BoardPost['kind']>('general');
  const [err, setErr] = useState<string | null>(null);

  async function send() {
    setErr(null);
    try { await postToBoard(body, kind); setBody(''); reload(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  return (
    <section className="today-block">
      <header className="block-head"><h2>Noticeboard</h2></header>

      {/* Open to everyone by design. A social board only the leadership can post to is a
          bulletin, not a board. */}
      <div className="board-compose card">
        <textarea rows={2} placeholder="Share something with the team" value={body}
                  onChange={(e) => setBody(e.target.value)} />
        <div className="composer-row">
          <select value={kind} onChange={(e) => setKind(e.target.value as BoardPost['kind'])}>
            <option value="general">General</option>
            <option value="kudos">Kudos</option>
            <option value="question">Question</option>
            <option value="social">Social</option>
            <option value="for_sale">For sale</option>
          </select>
          <button className="btn-save" disabled={!body.trim()} onClick={send}>Post</button>
        </div>
        {err && <p className="composer-err" role="alert">{err}</p>}
      </div>

      {posts.map((p) => (
        <article key={p.id} className="card board-post">
          <div className="board-head">
            <strong>{p.author_name ?? 'A colleague'}</strong>
            <span className="pill mute">{p.kind.replace('_', ' ')}</span>
            <span className="board-when">
              {new Date(p.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            </span>
          </div>
          <p>{p.body}</p>
        </article>
      ))}
    </section>
  );
}

/* -------------------------------------------------------------------------------------
   The page
   ------------------------------------------------------------------------------------- */
export function TodayView() {
  const [weather, setWeather] = useState<Weather | null>(null);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [events, setEvents] = useState<CompanyEvent[]>([]);
  const [posts, setPosts] = useState<BoardPost[]>([]);
  const [slt, setSlt] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);
  const [inbox, setInbox] = useState<InboxItem[]>([]);
  const [inboxLoading, setInboxLoading] = useState(true);

  // The second argument upgrades the reading once the browser grants a location.
  useEffect(() => { fetchWeather(setWeather).then((w) => w && setWeather(w)); }, []);
  useEffect(() => {
    setInboxLoading(true);
    fetchInbox().then(setInbox).catch(onFail('Your inbox', () => setInbox([]))).finally(() => setInboxLoading(false));
  }, [nonce]);
  useEffect(() => { canPublish().then(setSlt).catch(onFail('Your inbox', () => setSlt(false))); }, []);

  useEffect(() => {
    let live = true;
    Promise.all([fetchNotices(), fetchNextEvents(), fetchBoard()])
      .then(([n, e, p]) => { if (!live) return; setNotices(n); setEvents(e); setPosts(p); setLoadError(null); })
      // One failing panel must not blank the landing page, so the error is shown in place.
      .catch((e) => live && setLoadError(e instanceof Error ? e.message : String(e)));
    return () => { live = false; };
  }, [nonce]);

  const [diary, setDiary] = useState<DiaryEntry[]>([]);
  useEffect(() => { fetchDiary().then(setDiary).catch(onFail('The diary', () => setDiary([]))); }, [nonce]);

  const sorted = useMemo(() =>
    diary.map((e) => ({ ...e, daysAway: daysAway(e.due_date) }))
         .sort((a, b) => a.daysAway - b.daysAway), [diary]);
  const groups = useMemo(() => sorted.reduce<Record<string, typeof sorted>>((a, e) => {
    (a[bucket(e.daysAway)] ||= []).push(e);
    return a;
  }, {}), [sorted]);

  return (
    <div className="page today">
      <Clock weather={weather} />

      {loadError && (
        <p className="block-error" role="alert">
          Could not load notices: {loadError}
        </p>
      )}

      {/* Everything that will not resolve until you personally do something, gathered from
          four modules that each used to hide their own. */}
      <WaitingOnYou items={inbox} loading={inboxLoading} />

      <div className="today-columns">
        <div className="today-col-main">
          <Notices notices={notices} slt={slt} reload={reload} />
        </div>
        <div className="today-col-side">
          <NextEvents events={events} slt={slt} reload={reload} />
          <Noticeboard posts={posts} reload={reload} />
        </div>
      </div>

      <div className="page-head today-diary-head">
        <h2 className="page-title">Deadlines</h2>
        <p className="page-sub">
          One diary for the whole company. Every deadline that can be missed silently appears
          here, regardless of which module owns it.
        </p>
      </div>

      <div className="today-summary">
        <div className="card ts-card ts-critical">
          <div className="ts-n">{sorted.filter((e) => e.severity === 'critical').length}</div>
          <div className="ts-l">Critical</div>
        </div>
        <div className="card ts-card">
          <div className="ts-n">{sorted.filter((e) => e.daysAway <= 30).length}</div>
          <div className="ts-l">Due within 30 days</div>
        </div>
        <div className="card ts-card">
          <div className="ts-n">{sorted.length}</div>
          <div className="ts-l">Open items</div>
        </div>
      </div>

      {sorted.length === 0 && (
        <p className="block-empty">
          Nothing in the diary. It is built from Docket deadlines, reimbursement windows and
          contract notice periods, so an empty diary usually means those are not populated yet
          rather than that there is nothing to do.
        </p>
      )}

      {Object.entries(groups).map(([label, items]) => (
        <section key={label} className="today-group">
          <h2 className="today-group-title">{label}</h2>
          <div className="today-list">
            {items.map((e) => (
              <article key={e.id} className={`card entry sev-${e.severity}`}>
                <div className="entry-when">
                  <div className="entry-days">{e.daysAway}</div>
                  <div className="entry-days-l">days</div>
                </div>
                <div className="entry-main">
                  <header className="entry-head">
                    <h3>{e.title}</h3>
                    <span className={`pill ${e.severity === 'critical' ? 'risk' : e.severity === 'high' ? 'warn' : 'mute'}`}>
                      {sevLabel[e.severity]}
                    </span>
                  </header>
                  <p className="entry-detail">{e.detail}</p>
                  <footer className="entry-foot">
                    <span className="pill mute">{e.category.replace(/_/g, ' ')}</span>
                    <span className="entry-src">{e.source_system}</span>
                    <span className="entry-date">
                      {new Date(e.due_date).toLocaleDateString('en-GB',
                        { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                    <button className="link-btn"
                            onClick={() => completeDiaryEntry(e.id).then(reload)}>Done</button>
                  </footer>
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/* =====================================================================================
   Taking a notice down.

   Retract is the default and the right action almost always: the notice disappears from every
   screen and the record of what people were told, with any signatures against it, survives.

   Delete exists for the case where the content itself is the problem — something offensive,
   or confidential information posted by mistake. It destroys the body. The database refuses it
   where people have acknowledged the notice, because those signatures are evidence somebody
   was told something and destroying what they signed destroys the evidence.
   ===================================================================================== */

function RemoveNotice({ item, onClose, onDone }: {
  item: { n: Notice; mode: 'retract' | 'delete' };
  onClose: () => void; onDone: () => void;
}) {
  const ref = useDialog(onClose);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const hard = item.mode === 'delete';

  async function go() {
    setBusy(true); setErr(null);
    try {
      if (hard) await deleteNotice(item.n.id, reason);
      else await retractNotice(item.n.id, reason);
      onDone();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="cf-scrim" onClick={onClose} />
      <div className="cf" role="dialog" aria-modal="true" ref={ref}
           aria-label={hard ? 'Delete this notice' : 'Take this notice down'}
           onClick={(e) => e.stopPropagation()}>
        <header className="cf-head">
          <h2>{hard ? 'Delete this notice' : 'Take this notice down'}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="cf-body">
          <p className="cf-note"><strong>{item.n.title}</strong></p>
          <p className="cf-note">
            {hard
              ? 'The content will be destroyed and cannot be recovered. A record that a notice '
                + 'was removed, by whom and why, is kept. The content is not. Use this where '
                + 'the content itself is the problem.'
              : 'It disappears from every screen immediately. The record, and any '
                + 'acknowledgements against it, are kept.'}
          </p>
          <label className="cf-field">
            <span>Why (recorded, and required)</span>
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder={hard ? 'Offensive content posted in error.'
                                        : 'Superseded by the revised policy.'} />
          </label>
          {err && <p className="composer-err" role="alert">{err}</p>}
          <div className="cf-actions">
            <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
            <button className={hard ? 'btn-danger' : 'btn-save'} onClick={go}
                    disabled={busy || reason.trim().length < 3}>
              {busy ? 'Working' : hard ? 'Delete permanently' : 'Take down'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
